import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useBrowserNotifications } from './useBrowserNotifications'
import { useAuthStore } from '~/stores/auth'

/**
 * Notifications composable with WebSocket real-time updates + polling fallback.
 *
 * Connection states:
 * - 'connected': WebSocket is connected (real-time updates)
 * - 'polling': Using polling fallback
 * - 'connecting': Attempting to connect WebSocket
 * - 'disconnected': Not connected
 */

export interface AppNotification {
  id: number
  user_id: number | null
  app_id: number | null
  title: string | null
  message: string
  type: string
  category: string
  action_url: string | null
  status: string
  created_at: string
}

const notifications = ref<AppNotification[]>([])
const unreadCount = computed(() => notifications.value.filter(n => n.status === 'unread').length)
const connectionStatus = ref<'connecting' | 'connected' | 'disconnected' | 'polling'>('disconnected')

let browserNotifications: ReturnType<typeof useBrowserNotifications> | null = null
function getBrowserNotifications() {
  if (!browserNotifications) {
    browserNotifications = useBrowserNotifications()
  }
  return browserNotifications
}

let ws: WebSocket | null = null
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 8
const RECONNECT_BASE_DELAY = 2000
let pollingInterval: ReturnType<typeof setInterval> | null = null
const POLLING_INTERVAL = 15000 // 15 seconds
let pingInterval: ReturnType<typeof setInterval> | null = null

function getToken(): string | null {
  const authStore = useAuthStore()
  return authStore.token || useCookie('auth-token').value || null
}

function formatDate(dateString: string) {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleString()
}

function showBrowserNotif(notification: AppNotification) {
  const bn = getBrowserNotifications()
  if (!bn.shouldShowNotifications.value || !notification.message) return
  const validType = ['info', 'success', 'warning', 'error'].includes(notification.type)
    ? notification.type as 'info' | 'success' | 'warning' | 'error'
    : 'info'
  bn.notifyAppEvent({
    appName: notification.title || 'SaaS App',
    message: notification.message,
    type: validType,
    onClick: () => {
      if (notification.action_url) {
        window.location.href = notification.action_url
      } else {
        window.location.href = '/notifications'
      }
    }
  })
}

/** Merge a real-time notification into the list without re-fetching. */
function mergeNotification(incoming: AppNotification) {
  const exists = notifications.value.find(n => n.id === incoming.id)
  if (!exists) {
    notifications.value.unshift(incoming)
    showBrowserNotif(incoming)
  }
}

async function fetchNotifications() {
  const token = getToken()
  if (!token) return

  try {
    const res = await fetch('/api/v1/notifications?limit=100', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (res.ok) {
      const fresh: AppNotification[] = await res.json()

      // Detect new items for browser notifications
      if (notifications.value.length > 0) {
        const existingIds = new Set(notifications.value.map(n => n.id))
        fresh.filter(n => !existingIds.has(n.id)).forEach(showBrowserNotif)
      }

      notifications.value = fresh
    }
  } catch {
    // Silently fail — will retry on next poll/WS message
  }
}

async function markAsRead(notification: AppNotification) {
  if (notification.status === 'read') return
  const token = getToken()
  if (!token) return
  try {
    await fetch(`/api/v1/notifications/${notification.id}/read`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    notification.status = 'read'
  } catch { /* retry next time */ }
}

async function markAllAsRead() {
  const token = getToken()
  if (!token) return
  try {
    await fetch('/api/v1/notifications/mark-all-read', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    notifications.value.forEach(n => { n.status = 'read' })
  } catch { /* retry next time */ }
}

async function deleteNotification(id: number) {
  const token = getToken()
  if (!token) return
  try {
    await fetch(`/api/v1/notifications/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    notifications.value = notifications.value.filter(n => n.id !== id)
  } catch { /* retry next time */ }
}

function startPolling() {
  if (pollingInterval) clearInterval(pollingInterval)
  pollingInterval = setInterval(fetchNotifications, POLLING_INTERVAL)
  connectionStatus.value = 'polling'
}

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
}

function stopPing() {
  if (pingInterval) {
    clearInterval(pingInterval)
    pingInterval = null
  }
}

function connectWebSocket() {
  const token = getToken()
  if (!token) {
    startPolling()
    return
  }

  connectionStatus.value = 'connecting'
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const wsHost = window.location.hostname
  const wsPort = isDev ? ':5550' : (window.location.port ? `:${window.location.port}` : '')
  const wsUrl = `${protocol}//${wsHost}${wsPort}/api/v1/ws/notifications?token=${token}`

  try {
    ws = new WebSocket(wsUrl)
  } catch {
    startPolling()
    return
  }

  ws.onopen = () => {
    connectionStatus.value = 'connected'
    reconnectAttempts = 0
    stopPolling()
    // Keep-alive ping every 25 seconds
    stopPing()
    pingInterval = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send('ping')
      }
    }, 25000)
  }

  ws.onmessage = (event) => {
    if (event.data === 'pong') return
    try {
      const data = JSON.parse(event.data)
      if (data.id) {
        mergeNotification(data as AppNotification)
      } else {
        // Fallback: if the payload doesn't look like a notification, refetch
        fetchNotifications()
      }
    } catch {
      fetchNotifications()
    }
  }

  ws.onerror = () => {
    connectionStatus.value = 'disconnected'
    stopPing()
    if (!pollingInterval) startPolling()
    scheduleReconnect()
  }

  ws.onclose = () => {
    connectionStatus.value = 'disconnected'
    stopPing()
    if (!pollingInterval) startPolling()
    scheduleReconnect()
  }
}

function scheduleReconnect() {
  if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
    const delay = RECONNECT_BASE_DELAY * Math.pow(1.5, reconnectAttempts)
    reconnectAttempts++
    setTimeout(connectWebSocket, delay)
  }
}

let isInitialized = false

export function useNotifications() {
  const authStore = useAuthStore()

  const initializeNotifications = () => {
    if (isInitialized) return
    const token = getToken()
    if (!token || !authStore.isAuthenticated) return

    isInitialized = true
    fetchNotifications()
    connectWebSocket()
  }

  try {
    onMounted(async () => {
      await new Promise(resolve => setTimeout(resolve, 100))
      initializeNotifications()
    })

    watch(() => authStore.isAuthenticated, (isAuth) => {
      if (isAuth && !isInitialized) {
        setTimeout(initializeNotifications, 100)
      } else if (!isAuth && isInitialized) {
        if (ws) { ws.onclose = null; ws.close() }
        stopPolling()
        stopPing()
        isInitialized = false
        notifications.value = []
      }
    }, { immediate: true })

    onUnmounted(() => {
      if (ws) { ws.onclose = null; ws.close() }
      stopPolling()
      stopPing()
      isInitialized = false
    })
  } catch {
    initializeNotifications()
  }

  return {
    notifications,
    unreadCount,
    connectionStatus,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    formatDate,
    refresh: fetchNotifications,
    reinitialize: () => {
      isInitialized = false
      initializeNotifications()
    },
    cleanup: () => {
      if (ws) { ws.onclose = null; ws.close() }
      stopPolling()
      stopPing()
      isInitialized = false
    }
  }
}
